// hooks/useMeteoraBinLiquidity.js
//
// Polls Meteora DLMM bin reserves for the currently-subscribed token and
// returns a { bins, activeBinPrice, loading, error } snapshot.
//
// Strategy
// --------
// 1. Wait for a Meteora DLMM pool pubkey — either passed in directly (from
//    the backend's `subscribed` / `meteora_pool` message) or resolved via
//    DexScreener as a fallback.
// 2. Lazy-load @meteora-ag/dlmm and @solana/web3.js only when a pool is
//    known. First load is ~200KB gzipped; subsequent tokens re-use it.
// 3. Call dlmm.getBinsAroundActiveBin(N, N) every POLL_MS. Return normalized
//    bins keyed by mcap so the canvas can bucket-align them.
// 4. Halt polling when token/pool changes or the hook unmounts.
//
// Returned bin shape:
//   { price,       // SOL per token, for reference
//     mcap,        // price * supply — directly comparable to chart's mcap
//     solReserve,  // bin's Y reserve (SOL)
//     tokenReserve,// bin's X reserve (token)
//     notionalSol, // solReserve + tokenReserve * price (rough)
//     side }       // 'bid' | 'ask' | 'active' relative to active bin

import { useState, useEffect, useRef } from 'react'

const POLL_MS = 3000
const BINS_EACH_SIDE = 100
const SOL_MINT = 'So11111111111111111111111111111111111111112'

// One shared dynamic import across all hook instances / token switches
let _sdkPromise = null
async function loadSDK() {
  if (!_sdkPromise) {
    _sdkPromise = (async () => {
      const [{ default: DLMM }, { Connection, PublicKey }] = await Promise.all([
        import('@meteora-ag/dlmm'),
        import('@solana/web3.js'),
      ])
      return { DLMM, Connection, PublicKey }
    })()
  }
  return _sdkPromise
}

// Fallback resolver — hit DexScreener if the backend hasn't sent us a pool.
// Returns the biggest Meteora DLMM pool for the token, or null.
async function resolveMeteoraPoolFromDexScreener(mint) {
  try {
    const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mint}`)
    const data = await r.json()
    const pairs = (data.pairs || []).filter(
      (p) => p.chainId === 'solana' && /meteora/i.test(p.dexId || '')
    )
    if (!pairs.length) return null
    pairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))
    return pairs[0].pairAddress || null
  } catch {
    return null
  }
}

export default function useMeteoraBinLiquidity({
  mint,
  pool,            // optional — backend-provided pubkey (preferred)
  supply,          // token supply for price → mcap conversion
  rpcUrl,          // same RPC you use elsewhere
  tokenDecimals = 6, // default for pump.fun-era tokens
  enabled = true,  // disable in HL mode or when feature-flagged off
}) {
  const [bins, setBins] = useState([])
  const [activeBinPrice, setActiveBinPrice] = useState(0)
  const [error, setError] = useState(null)

  // Guard against races when token/pool changes mid-poll
  const runIdRef = useRef(0)
  const dlmmInstanceRef = useRef(null)
  const lastPoolRef = useRef(null)

  useEffect(() => {
    if (!enabled || !mint || !rpcUrl || !supply) {
      setBins([])
      setActiveBinPrice(0)
      return
    }

    const myRunId = ++runIdRef.current
    let cancelled = false
    let timer = null

    // If the pool changed, drop the cached DLMM instance — it's pool-scoped.
    if (pool !== lastPoolRef.current) {
      dlmmInstanceRef.current = null
      lastPoolRef.current = pool
    }

    async function ensureInstance() {
      if (dlmmInstanceRef.current) return dlmmInstanceRef.current

      let poolPubkey = pool
      if (!poolPubkey) {
        poolPubkey = await resolveMeteoraPoolFromDexScreener(mint)
        if (!poolPubkey) return null
      }

      const { DLMM, Connection, PublicKey } = await loadSDK()
      if (cancelled || myRunId !== runIdRef.current) return null

      const connection = new Connection(rpcUrl, 'confirmed')
      try {
        const instance = await DLMM.create(connection, new PublicKey(poolPubkey))
        if (cancelled || myRunId !== runIdRef.current) return null
        dlmmInstanceRef.current = instance
        return instance
      } catch (e) {
        if (!cancelled) setError(`DLMM init: ${e.message}`)
        return null
      }
    }

    async function pollOnce() {
      if (cancelled || myRunId !== runIdRef.current) return
      const dlmm = await ensureInstance()
      if (!dlmm) return

      try {
        const active = await dlmm.getActiveBin()
        const binList = await dlmm.getBinsAroundActiveBin(BINS_EACH_SIDE, BINS_EACH_SIDE)
        if (cancelled || myRunId !== runIdRef.current) return

        const activePrice = Number(active.pricePerToken || active.price || 0)
        setActiveBinPrice(activePrice * supply) // mcap form

        // Normalize. SDK shape varies across versions; accept either.
        const raw = binList?.bins || binList || []
        const normalized = raw.map((b) => {
          const price = Number(b.pricePerToken || b.price || 0)
          const xAmount = Number(b.xAmount || 0) // token side
          const yAmount = Number(b.yAmount || 0) // quote (SOL/USDC) side
          // Heuristic: if the pool's quote is SOL (native decimals = 9),
          // yAmount is lamports. We can't know the quote mint from bins
          // alone without extra fetches, so we assume SOL for now.
          const solReserve = yAmount / 1e9
          const tokenReserve = xAmount / Math.pow(10, tokenDecimals)
          const notionalSol = solReserve + tokenReserve * price
          const side = b.binId < active.binId ? 'bid'
                     : b.binId > active.binId ? 'ask'
                     : 'active'
          return {
            binId: b.binId,
            price,
            mcap: price * supply,
            solReserve,
            tokenReserve,
            notionalSol,
            side,
          }
        }).filter((b) => b.mcap > 0 && b.notionalSol > 0)

        setBins(normalized)
        setError(null)
      } catch (e) {
        if (!cancelled) setError(`DLMM poll: ${e.message}`)
      }
    }

    // First poll immediately, then on interval
    pollOnce()
    timer = setInterval(pollOnce, POLL_MS)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
  }, [mint, pool, supply, rpcUrl, enabled])

  return { bins, activeBinPrice, error }
}
